import { HttpClient } from './http'
import { readWarungToken } from './warungToken'
import type { ActivationCodeResult, CashMovementInput, CustomerInput, DebtInput, DebtPaymentInput, ProductInput, PurchaseInput, PurchasePaymentInput, ShoppingItemInput, StockAdjustmentInput, StockCountInput, SupplierInput, WarungBusinessReport, WarungCashMovement, WarungFinancePage, WarungCustomer, WarungDashboard, WarungDebt, WarungInventoryWorkspace, PublicGoogleRegistrationInput, PublicRegistrationInput, RegistrationResult, WarungProduct, WarungPurchase, WarungSession, WarungShoppingItem, WarungStockCount, WarungSubscription, WarungSupplier, WarungUser, WarungAccountProfile, WarungDevice, WarungDevicesResult, SaleInput, WarungSale, ConsignmentWorkspace, ConsignorInput, Consignor, ConsignmentItemInput, ConsignmentItem, ConsignmentSale, ConsignmentSettlement, ConsignmentAdjustment, ConsignmentOwnerReport, PaginatedResult, PaginationQuery, ProductListQuery, ProductOptions, FundAccount, FundAccountInput, FundTransfer, CashSession, CashSessionSummary, ManagedWarungUser, ManagedWarungUserInput, ManagedWarungUserCreateResult, ResetWarungUserAccessResult, WarungBusinessInsights } from '@/types/warung'

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '')
const http = new HttpClient(apiBaseUrl, readWarungToken)

interface ApiPaginationMeta {
  readonly page: number
  readonly per_page: number
  readonly total: number
  readonly total_pages: number
}

interface ApiPaginatedResult<T> {
  readonly items: readonly T[]
  readonly meta: ApiPaginationMeta
}

interface ApiConsignmentWorkspace extends Omit<ConsignmentWorkspace, 'owners_meta' | 'items_meta'> {
  readonly owners_meta: ApiPaginationMeta
  readonly items_meta: ApiPaginationMeta
}

interface ApiInventoryWorkspace extends Omit<WarungInventoryWorkspace, 'products_meta' | 'movements_meta' | 'counts_meta'> {
  readonly products_meta: ApiPaginationMeta
  readonly movements_meta: ApiPaginationMeta
  readonly counts_meta: ApiPaginationMeta
}

type PageQuery = PaginationQuery | (PaginationQuery & Readonly<Record<string, string | number | boolean | undefined>>)

function pagePath(path: string, query: PageQuery = {}): string {
  const params = new URLSearchParams()
  if (query.page !== undefined) params.set('page', String(query.page))
  if (query.perPage !== undefined) params.set('per_page', String(query.perPage))
  if (query.search !== undefined && query.search.trim() !== '') params.set('search', query.search.trim())
  for (const [key, value] of Object.entries(query)) {
    if (key === 'page' || key === 'perPage' || key === 'search' || value === undefined || value === '') continue
    params.set(key, String(value))
  }
  const suffix = params.toString()
  return suffix === '' ? path : `${path}?${suffix}`
}

async function paged<T>(path: string, query: PageQuery = {}): Promise<PaginatedResult<T>> {
  const result = await http.get<ApiPaginatedResult<T>>(pagePath(path, query))
  return {
    items: result.items,
    meta: {
      page: result.meta.page,
      perPage: result.meta.per_page,
      total: result.meta.total,
      totalPages: result.meta.total_pages,
    },
  }
}

function jakartaBusinessDate(value = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

export const warungApi = {
  register(input: PublicRegistrationInput): Promise<RegistrationResult> {
    return http.post<RegistrationResult>('/public/register', input, { authenticated: false })
  },
  registerWithGoogle(input: PublicGoogleRegistrationInput): Promise<RegistrationResult> {
    return http.post<RegistrationResult>('/public/register/google', input, { authenticated: false })
  },
  login(input: { readonly identifier: string; readonly password: string }): Promise<WarungSession> {
    return http.post<WarungSession>('/warung/auth/login', input, { authenticated: false })
  },
  loginWithGoogle(credential: string): Promise<WarungSession> {
    return http.post<WarungSession>('/warung/auth/google', { credential }, { authenticated: false })
  },
  me(): Promise<WarungUser> {
    return http.get<WarungUser>('/warung/auth/me')
  },
  logout(): Promise<{ readonly ok: boolean }> {
    return http.post<{ readonly ok: boolean }>('/warung/auth/logout', {})
  },
  changePassword(currentPassword: string, newPassword: string): Promise<{ readonly ok: boolean }> {
    return http.post<{ readonly ok: boolean }>('/warung/auth/change-password', { currentPassword, newPassword })
  },
  forgotPassword(email: string): Promise<{ readonly ok: boolean; readonly message: string }> {
    return http.post<{ readonly ok: boolean; readonly message: string }>('/public/password/forgot', { email }, { authenticated: false })
  },
  resetPassword(token: string, password: string): Promise<{ readonly ok: boolean }> {
    return http.post<{ readonly ok: boolean }>('/public/password/reset', { token, password }, { authenticated: false })
  },
  verifyEmail(token: string): Promise<{ readonly ok: boolean }> {
    return http.post<{ readonly ok: boolean }>('/public/email/verify', { token }, { authenticated: false })
  },
  account(): Promise<WarungAccountProfile> {
    return http.get<WarungAccountProfile>('/warung/account')
  },
  updateAccount(input: { readonly displayName: string; readonly businessName: string; readonly ownerName: string; readonly email: string; readonly phone: string; readonly area: string; readonly currentPassword?: string }): Promise<WarungAccountProfile> {
    return http.patch<WarungAccountProfile>('/warung/account', input)
  },
  resendVerification(): Promise<{ readonly sent: boolean; readonly expiresAt: string | null }> {
    return http.post<{ readonly sent: boolean; readonly expiresAt: string | null }>('/warung/account/email/resend', {})
  },
  linkGoogle(credential: string): Promise<WarungAccountProfile> {
    return http.post<WarungAccountProfile>('/warung/account/google/link', { credential })
  },
  unlinkGoogle(): Promise<WarungAccountProfile> {
    return http.delete<WarungAccountProfile>('/warung/account/google')
  },
  requestAccountDeletion(confirmation: string): Promise<{ readonly requestedAt: string; readonly scheduledFor: string }> {
    return http.post<{ readonly requestedAt: string; readonly scheduledFor: string }>('/warung/account/delete-request', { confirmation })
  },
  cancelAccountDeletion(): Promise<{ readonly ok: boolean }> {
    return http.delete<{ readonly ok: boolean }>('/warung/account/delete-request')
  },
  subscription(): Promise<WarungSubscription> {
    return http.get<WarungSubscription>('/warung/subscription')
  },
  deviceAccessCode(): Promise<ActivationCodeResult> {
    return http.post<ActivationCodeResult>('/warung/device-access-code', {})
  },
  activationCode(): Promise<ActivationCodeResult> {
    return http.post<ActivationCodeResult>('/warung/device-access-code', {})
  },
  devices(): Promise<WarungDevicesResult> {
    return http.get<WarungDevicesResult>('/warung/devices')
  },
  renameDevice(deviceId: string, label: string): Promise<WarungDevice> {
    return http.patch<WarungDevice>(`/warung/devices/${encodeURIComponent(deviceId)}`, { label })
  },
  revokeDevice(deviceId: string): Promise<{ readonly ok: boolean }> {
    return http.post<{ readonly ok: boolean }>(`/warung/devices/${encodeURIComponent(deviceId)}/revoke`, {})
  },
  deleteDevice(deviceId: string): Promise<{ readonly ok: boolean }> {
    return http.delete<{ readonly ok: boolean }>(`/warung/devices/${encodeURIComponent(deviceId)}`)
  },
  dashboard(): Promise<WarungDashboard> {
    return http.get<WarungDashboard>('/warung/dashboard')
  },
  entities(entityType: string, query: PaginationQuery & { readonly pending?: boolean } = {}): Promise<PaginatedResult<Record<string, unknown>>> {
    return paged<Record<string, unknown>>(`/warung/entities/${encodeURIComponent(entityType)}`, { ...query, pending: query.pending === true ? 1 : undefined })
  },
  sales(query: PaginationQuery = {}): Promise<PaginatedResult<WarungSale>> {
    return paged<WarungSale>('/warung/sales', query)
  },
  async createSale(input: SaleInput, idempotencyKey?: string): Promise<WarungSale> {
    let cashSessionId: string | null = null
    try {
      const session = await http.get<CashSession>(`/warung/cash-sessions/${encodeURIComponent(jakartaBusinessDate())}`)
      if (session.status === 'open') cashSessionId = session.id
    } catch {
      // Kas Harian tetap opsional untuk transaksi. Jika sesi belum dibuka,
      // penjualan dikirim dengan relasi null dan API tetap menerima transaksi.
    }
    return http.post<WarungSale>('/warung/sales', { ...input, cash_session_id: cashSessionId }, {
      ...(idempotencyKey === undefined || idempotencyKey.trim() === ''
        ? {}
        : { headers: { 'Idempotency-Key': idempotencyKey } }),
    })
  },
  cancelSale(id: string, reason: string, idempotencyKey?: string): Promise<Record<string, unknown>> {
    return http.post<Record<string, unknown>>(`/warung/sales/${encodeURIComponent(id)}/cancel`, { reason }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  consignments(query: { readonly ownerPage?: number; readonly ownerPerPage?: number; readonly itemPage?: number; readonly itemPerPage?: number } = {}): Promise<ConsignmentWorkspace> {
    const params = new URLSearchParams()
    if (query.ownerPage !== undefined) params.set('owner_page', String(query.ownerPage))
    if (query.ownerPerPage !== undefined) params.set('owner_per_page', String(query.ownerPerPage))
    if (query.itemPage !== undefined) params.set('item_page', String(query.itemPage))
    if (query.itemPerPage !== undefined) params.set('item_per_page', String(query.itemPerPage))
    const suffix = params.toString()
    return http.get<ApiConsignmentWorkspace>(`/warung/consignments${suffix === '' ? '' : `?${suffix}`}`).then((result) => ({
      ...result,
      owners_meta: {
        page: result.owners_meta.page,
        perPage: result.owners_meta.per_page,
        total: result.owners_meta.total,
        totalPages: result.owners_meta.total_pages,
      },
      items_meta: {
        page: result.items_meta.page,
        perPage: result.items_meta.per_page,
        total: result.items_meta.total,
        totalPages: result.items_meta.total_pages,
      },
    }))
  },
  consignmentHistory(kind: 'sales' | 'settlements' | 'adjustments', query: PaginationQuery = {}): Promise<PaginatedResult<ConsignmentSale | ConsignmentSettlement | ConsignmentAdjustment>> {
    return paged<ConsignmentSale | ConsignmentSettlement | ConsignmentAdjustment>(`/warung/consignments/history`, { ...query, kind })
  },
  createConsignor(input: ConsignorInput, idempotencyKey?: string): Promise<Consignor> {
    return http.post<Consignor>('/warung/consignments/owners', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  createConsignmentItem(input: ConsignmentItemInput, idempotencyKey?: string): Promise<ConsignmentItem> {
    return http.post<ConsignmentItem>('/warung/consignments/items', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  receiveConsignmentItem(id: string, quantity: number, note?: string, idempotencyKey?: string): Promise<Record<string, unknown>> {
    return http.post<Record<string, unknown>>(`/warung/consignments/items/${encodeURIComponent(id)}/receive`, { quantity, note }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  settleConsignor(id: string, amount: number, paymentMethod: string, notes?: string, fundAccountId?: string | null, idempotencyKey?: string): Promise<ConsignmentSettlement> {
    return http.post<ConsignmentSettlement>(`/warung/consignments/owners/${encodeURIComponent(id)}/settle`, { amount, payment_method: paymentMethod, notes, fund_account_id: fundAccountId ?? null }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  updateConsignmentItem(id: string, input: ConsignmentItemInput): Promise<ConsignmentItem> {
    return http.patch<ConsignmentItem>(`/warung/consignments/items/${encodeURIComponent(id)}`, input)
  },
  adjustConsignmentItem(id: string, type: 'return' | 'lost' | 'damaged', quantity: number, note?: string, idempotencyKey?: string): Promise<Record<string, unknown>> {
    return http.post<Record<string, unknown>>(`/warung/consignments/items/${encodeURIComponent(id)}/adjust`, { type, quantity, note }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  paymentAccounts(): Promise<readonly FundAccount[]> {
    return http.get<{ readonly items: readonly FundAccount[] }>('/warung/payment-accounts').then((result) => result.items)
  },
  fundAccounts(): Promise<readonly FundAccount[]> {
    return http.get<{ readonly items: readonly FundAccount[] }>('/warung/fund-accounts').then((result) => result.items)
  },
  createFundAccount(input: FundAccountInput, idempotencyKey?: string): Promise<FundAccount> {
    return http.post<FundAccount>('/warung/fund-accounts', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  updateFundAccount(id: string, input: Partial<FundAccountInput> & { readonly is_active?: boolean; readonly _version?: number }): Promise<FundAccount> {
    return http.patch<FundAccount>(`/warung/fund-accounts/${encodeURIComponent(id)}`, input)
  },
  fundTransfers(query: PaginationQuery = {}): Promise<PaginatedResult<FundTransfer>> {
    return paged<FundTransfer>('/warung/fund-transfers', query)
  },
  createFundTransfer(input: { readonly source_fund_account_id: string; readonly destination_fund_account_id: string; readonly amount: number; readonly note?: string | null; readonly transferred_at?: string }, idempotencyKey?: string): Promise<FundTransfer> {
    return http.post<FundTransfer>('/warung/fund-transfers', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  cashSession(date: string): Promise<CashSession> {
    return http.get<CashSession>(`/warung/cash-sessions/${encodeURIComponent(date)}`)
  },
  cashSessionSummary(date: string): Promise<CashSessionSummary> {
    return http.get<CashSessionSummary>(`/warung/cash-sessions/${encodeURIComponent(date)}/summary`)
  },
  openCashSession(input: { readonly business_date: string; readonly opening_amount: number; readonly opening_note?: string | null }, idempotencyKey?: string): Promise<CashSession> {
    return http.post<CashSession>('/warung/cash-sessions/open', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  closeCashSession(id: string, input: { readonly expected_closing_amount: number; readonly actual_closing_amount: number; readonly closing_note?: string | null; readonly _version?: number }, idempotencyKey?: string): Promise<CashSession> {
    return http.post<CashSession>(`/warung/cash-sessions/${encodeURIComponent(id)}/close`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  correctCashSession(id: string, input: { readonly expected_closing_amount: number; readonly actual_closing_amount: number; readonly reason: string; readonly _version?: number }, idempotencyKey?: string): Promise<CashSession> {
    return http.post<CashSession>(`/warung/cash-sessions/${encodeURIComponent(id)}/correct`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  consignmentOwnerReport(id: string, days: 0 | 7 | 30 | 90 = 30): Promise<ConsignmentOwnerReport> {
    const period = days === 0 ? 'all' : String(days)
    return http.get<ConsignmentOwnerReport>(`/warung/consignments/owners/${encodeURIComponent(id)}/report?days=${period}`)
  },
  products(query: ProductListQuery & { readonly barcode?: string } = {}): Promise<PaginatedResult<WarungProduct>> {
    return paged<WarungProduct>('/warung/products', query)
  },
  productOptions(): Promise<ProductOptions> {
    return http.get<ProductOptions>('/warung/products/options')
  },
  createProduct(input: ProductInput, idempotencyKey?: string): Promise<WarungProduct> {
    return http.post<WarungProduct>('/warung/products', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  updateProduct(id: string, input: ProductInput, idempotencyKey?: string): Promise<WarungProduct> {
    return http.patch<WarungProduct>(`/warung/products/${encodeURIComponent(id)}`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  archiveProduct(id: string, idempotencyKey?: string): Promise<WarungProduct> {
    return http.post<WarungProduct>(`/warung/products/${encodeURIComponent(id)}/archive`, {}, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  restoreProduct(id: string, idempotencyKey?: string): Promise<WarungProduct> {
    return http.post<WarungProduct>(`/warung/products/${encodeURIComponent(id)}/restore`, {}, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  customers(query: PaginationQuery & { readonly active?: number } = {}): Promise<PaginatedResult<WarungCustomer>> {
    return paged<WarungCustomer>('/warung/customers', query)
  },
  createCustomer(input: CustomerInput, idempotencyKey?: string): Promise<WarungCustomer> {
    return http.post<WarungCustomer>('/warung/customers', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  updateCustomer(id: string, input: CustomerInput, idempotencyKey?: string): Promise<WarungCustomer> {
    return http.patch<WarungCustomer>(`/warung/customers/${encodeURIComponent(id)}`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  debts(query: PaginationQuery & { readonly status?: string; readonly open?: number } = {}): Promise<PaginatedResult<WarungDebt>> {
    return paged<WarungDebt>('/warung/debts', query)
  },
  createDebt(input: DebtInput, idempotencyKey?: string): Promise<WarungDebt> {
    return http.post<WarungDebt>('/warung/debts', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  payDebt(id: string, input: DebtPaymentInput, idempotencyKey?: string): Promise<WarungDebt> {
    return http.post<WarungDebt>(`/warung/debts/${encodeURIComponent(id)}/payments`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  suppliers(query: PaginationQuery & { readonly active?: number } = {}): Promise<PaginatedResult<WarungSupplier>> {
    return paged<WarungSupplier>('/warung/suppliers', query)
  },
  createSupplier(input: SupplierInput, idempotencyKey?: string): Promise<WarungSupplier> {
    return http.post<WarungSupplier>('/warung/suppliers', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  updateSupplier(id: string, input: SupplierInput, idempotencyKey?: string): Promise<WarungSupplier> {
    return http.patch<WarungSupplier>(`/warung/suppliers/${encodeURIComponent(id)}`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  createShoppingItem(input: ShoppingItemInput, idempotencyKey?: string): Promise<WarungShoppingItem> {
    return http.post<WarungShoppingItem>('/warung/shopping-items', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  updateShoppingItem(id: string, input: ShoppingItemInput, idempotencyKey?: string): Promise<WarungShoppingItem> {
    return http.patch<WarungShoppingItem>(`/warung/shopping-items/${encodeURIComponent(id)}`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  deleteShoppingItem(id: string, version?: number, idempotencyKey?: string): Promise<Record<string, unknown>> {
    return http.delete<Record<string, unknown>>(`/warung/shopping-items/${encodeURIComponent(id)}`, { _version: version }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  purchases(query: PaginationQuery & { readonly open?: number } = {}): Promise<PaginatedResult<WarungPurchase>> {
    return paged<WarungPurchase>('/warung/purchases', query)
  },
  createPurchase(input: PurchaseInput, idempotencyKey?: string): Promise<WarungPurchase> {
    return http.post<WarungPurchase>('/warung/purchases', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  updatePurchase(id: string, input: PurchaseInput & { readonly _version: number }, idempotencyKey?: string): Promise<WarungPurchase> {
    return http.patch<WarungPurchase>(`/warung/purchases/${encodeURIComponent(id)}`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  payPurchase(id: string, input: PurchasePaymentInput, idempotencyKey?: string): Promise<WarungPurchase> {
    return http.post<WarungPurchase>(`/warung/purchases/${encodeURIComponent(id)}/payments`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  async finance(query: PaginationQuery & { readonly type?: string } = {}): Promise<WarungFinancePage> {
    const result = await http.get<ApiPaginatedResult<WarungCashMovement> & { readonly summary: WarungFinancePage['summary'] }>(pagePath('/warung/finance', query))
    return {
      items: result.items,
      meta: { page: result.meta.page, perPage: result.meta.per_page, total: result.meta.total, totalPages: result.meta.total_pages },
      summary: result.summary,
    }
  },
  createCashMovement(input: CashMovementInput, idempotencyKey?: string): Promise<WarungCashMovement> {
    return http.post<WarungCashMovement>('/warung/finance', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  async inventory(query: { readonly productPage?: number; readonly productPerPage?: number; readonly movementPage?: number; readonly movementPerPage?: number; readonly countPage?: number; readonly countPerPage?: number; readonly search?: string; readonly status?: string } = {}): Promise<WarungInventoryWorkspace> {
    const params = new URLSearchParams()
    if (query.productPage !== undefined) params.set('product_page', String(query.productPage))
    if (query.productPerPage !== undefined) params.set('product_per_page', String(query.productPerPage))
    if (query.movementPage !== undefined) params.set('movement_page', String(query.movementPage))
    if (query.movementPerPage !== undefined) params.set('movement_per_page', String(query.movementPerPage))
    if (query.countPage !== undefined) params.set('count_page', String(query.countPage))
    if (query.countPerPage !== undefined) params.set('count_per_page', String(query.countPerPage))
    if (query.search !== undefined && query.search.trim() !== '') params.set('search', query.search.trim())
    if (query.status !== undefined && query.status !== 'semua') params.set('status', query.status)
    const suffix = params.toString()
    const result = await http.get<ApiInventoryWorkspace>(`/warung/inventory${suffix === '' ? '' : `?${suffix}`}`)
    return {
      ...result,
      products_meta: { page: result.products_meta.page, perPage: result.products_meta.per_page, total: result.products_meta.total, totalPages: result.products_meta.total_pages },
      movements_meta: { page: result.movements_meta.page, perPage: result.movements_meta.per_page, total: result.movements_meta.total, totalPages: result.movements_meta.total_pages },
      counts_meta: { page: result.counts_meta.page, perPage: result.counts_meta.per_page, total: result.counts_meta.total, totalPages: result.counts_meta.total_pages },
    }
  },
  adjustStock(input: StockAdjustmentInput, idempotencyKey?: string): Promise<{ readonly product: WarungProduct }> {
    return http.post<{ readonly product: WarungProduct }>('/warung/inventory/adjustments', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  saveStockCount(input: StockCountInput, idempotencyKey?: string): Promise<WarungStockCount> {
    return http.post<WarungStockCount>('/warung/inventory/counts', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  users(): Promise<readonly ManagedWarungUser[]> {
    return http.get<readonly ManagedWarungUser[]>('/warung/users')
  },
  createUser(input: ManagedWarungUserInput & { readonly username: string }, idempotencyKey?: string): Promise<ManagedWarungUserCreateResult> {
    return http.post<ManagedWarungUserCreateResult>('/warung/users', input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  updateUser(id: string, input: ManagedWarungUserInput, idempotencyKey?: string): Promise<ManagedWarungUser> {
    return http.patch<ManagedWarungUser>(`/warung/users/${encodeURIComponent(id)}`, input, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  setUserActive(id: string, active: boolean, idempotencyKey?: string): Promise<ManagedWarungUser> {
    return http.patch<ManagedWarungUser>(`/warung/users/${encodeURIComponent(id)}/status`, { active }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
  },
  resetUserAccess(id: string): Promise<ResetWarungUserAccessResult> {
    return http.post<ResetWarungUserAccessResult>(`/warung/users/${encodeURIComponent(id)}/reset-access`, {})
  },
  reports(start: string, end: string): Promise<WarungBusinessReport> {
    const params = new URLSearchParams({ start, end })
    return http.get<WarungBusinessReport>(`/warung/reports?${params.toString()}`)
  },
  insights(period: 'today' | '30d' | 'month' | 'custom', options: { month?: string; start?: string; end?: string } = {}): Promise<WarungBusinessInsights> {
    const params = new URLSearchParams({ period })
    if (options.month) params.set('month', options.month)
    if (options.start) params.set('start', options.start)
    if (options.end) params.set('end', options.end)
    return http.get<WarungBusinessInsights>(`/warung/insights?${params.toString()}`)
  },
}
